import { Range } from 'react-date-range'

export const DATE_FORMATS = {
  US: 'MM-DD-YYYY',
  ISO: 'YYYY-MM-DD',
  EUROPEAN: 'DD-MM-YYYY',
  MONTH_YEAR: 'MM-YYYY',
} as const

export type DateFormat = (typeof DATE_FORMATS)[keyof typeof DATE_FORMATS]

/**
 * Formats a date according to the specified format
 * @param date The date to format
 * @param format The desired output format
 * @returns Formatted date string
 */
export const formatDate = (date: Date, format: DateFormat = DATE_FORMATS.US): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  switch (format) {
    case DATE_FORMATS.ISO:
      return `${year}-${month}-${day}`
    case DATE_FORMATS.EUROPEAN:
      return `${day}-${month}-${year}`
    case DATE_FORMATS.MONTH_YEAR:
      return `${month}-${year}`
    case DATE_FORMATS.US:
    default:
      return `${month}-${day}-${year}`
  }
}

/**
 * Attempts to parse a date string in various formats
 * @param dateStr The date string to parse
 * @returns Date object if valid, null if invalid
 */
export const parseDate = (dateStr: string): Date | null => {
  // Try different separators
  const separators = ['-', '/', '.']
  let parts: string[] = []

  // Find which separator is used
  for (const separator of separators) {
    if (dateStr.includes(separator)) {
      parts = dateStr.split(separator)
      break
    }
  }


  // Handle MM-YYYY format (2 parts)
  if (parts.length === 2) {
    const month = parseInt(parts[0])
    const year = parseInt(parts[1])

    // Basic validation
    if (month < 1 || month > 12) return null
    if (year < 1000 || year > 9999) return null

    // Return first day of the month
    const date = new Date(year, month - 1, 1)
    if (!isNaN(date.getTime()) && date.getMonth() === month - 1) {
      return date
    }
    return null
  }

  // Handle full date formats (3 parts)
  if (parts.length !== 3) return null

  // Try different date formats
  const formats = [
    // MM-DD-YYYY
    { month: 0, day: 1, year: 2 },
    // DD-MM-YYYY
    { month: 1, day: 0, year: 2 },
    // YYYY-MM-DD
    { month: 1, day: 2, year: 0 },
  ]

  for (const format of formats) {
    const month = parseInt(parts[format.month])
    const day = parseInt(parts[format.day])
    const year = parseInt(parts[format.year])

    // Basic validation
    if (month < 1 || month > 12) continue
    if (day < 1 || day > 31) continue
    if (year < 1000 || year > 9999) continue

    const date = new Date(year, month - 1, day)

    // Validate the date is valid and matches input
    if (!isNaN(date.getTime()) && date.getMonth() === month - 1 && date.getDate() === day) {
      return date
    }
  }

  return null
}

/**
 * Formats a date range for display
 * @param range The date range to format
 * @param format The desired output format
 * @returns Formatted date range string
 */
export const formatDateRange = (range: Range, format: DateFormat = DATE_FORMATS.US): string => {
  if (!range.startDate) return ''
  if (!range.endDate) return formatDate(range.startDate, format)
  return `${formatDate(range.startDate, format)} to ${formatDate(range.endDate, format)}`
}
